---
id: incident-analysis
title: Incident Root Cause Analysis
category: incident
difficulty: intermediate
tags:
  - incident
  - rca
  - postmortem
  - outage
  - troubleshooting
description: >
  Structures incident investigation by analyzing timeline, symptoms,
  and system state to identify root cause.
whenToUse: >
  During or immediately after an incident to organize findings and
  drive toward root cause.
variables:
  - name: severity
    desc: Incident severity (P1/P2/P3/P4)
  - name: duration
    desc: Total duration of the incident
  - name: affected_services
    desc: List of services impacted
  - name: customer_impact
    desc: How customers were affected
  - name: timeline
    desc: Chronological events with timestamps
  - name: metrics
    desc: Relevant metric anomalies observed
  - name: errors
    desc: Key error messages from logs
  - name: recent_changes
    desc: Recent deployments, config changes, infra changes
tips:
  - Include the full timeline with timestamps — even seemingly irrelevant events may correlate.
  - Note recent changes in the last 24-72 hours — most incidents are caused by changes.
  - Focus on systemic improvements in action items, not individual blame.
---

You are an incident commander performing root cause analysis.

## Incident Summary
- Severity: {{severity}}
- Duration: {{duration}}
- Services affected: {{affected_services}}
- Customer impact: {{customer_impact}}

## Timeline
{{timeline}}

## System State
- Metrics anomalies: {{metrics}}
- Error logs: {{errors}}
- Recent changes (deploy, config, infra): {{recent_changes}}

## Task
Perform structured root cause analysis:
1. **Impact Assessment** — Quantify user impact, blast radius
2. **Timeline Analysis** — Correlate events, find trigger point
3. **Root Cause** — Identify primary cause vs contributing factors
4. **5 Whys** — Drill to the systemic root cause
5. **Action Items** — Preventive measures, detection improvements

## Output Format (JSON)
{
  "title": "incident title for the postmortem",
  "impact": {
    "duration_minutes": 0,
    "users_affected": "estimate",
    "revenue_impact": "estimate if applicable",
    "sla_breach": true
  },
  "root_cause": {
    "primary": "one-sentence root cause",
    "contributing_factors": ["factor 1", "factor 2"],
    "five_whys": ["why 1", "why 2", "why 3", "why 4", "why 5"]
  },
  "action_items": [
    {
      "type": "prevent | detect | mitigate",
      "action": "description",
      "priority": "P1 | P2 | P3",
      "owner_suggestion": "team or role"
    }
  ],
  "detection_gap": "how could we have detected this sooner?"
}

<!-- EXAMPLE_OUTPUT -->

{
  "title": "Payment processing outage due to database connection pool exhaustion",
  "root_cause": {
    "primary": "A slow query caused by missing index exhausted the connection pool, cascading to all dependent services",
    "contributing_factors": ["No connection pool size alerting", "Missing index on frequently-queried column"],
    "five_whys": [
      "Why did payments fail? → Connection pool exhausted",
      "Why was the pool exhausted? → Connections held too long by slow queries",
      "Why were queries slow? → Missing index on orders.customer_id",
      "Why was the index missing? → No index review in PR process",
      "Why no index review? → No automated query plan analysis in CI"
    ]
  }
}
